import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, LogIn, MoreVertical, Plus, Search, Trash2 } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/alert-dialog.jsx";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { formatCurrency, formatDate } from "@/shared/lib/format.js";
import { useDebounced } from "@/shared/hooks/use-debounced.js";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { canWriteMaster } from "@/shared/lib/roles.js";
import { ClientStatusBadge, CLIENT_STATUS_OPTIONS } from "./components/MasterUi.jsx";
import {
  useCreateClient, useDeleteClient, useMasterClients, useMasterPlans,
  useStartImpersonation, useUpdateClient,
} from "./use-master.js";

/**
 * Carteira de clientes.
 *
 * O botão "Acessar ambiente" chama um endpoint que marca a visita na
 * sessão do servidor e registra em auditoria — no app antigo era só uma
 * escrita no `localStorage`.
 */
export function ClientsPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const podeEscrever = canWriteMaster(user);

  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("todos");
  const buscaAdiada = useDebounced(busca, 300);

  const { data: clientes = [], isLoading } = useMasterClients({
    search: buscaAdiada,
    status,
  });

  const entrar = useStartImpersonation();
  const excluir = useDeleteClient();

  const [novoAberto, setNovoAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState(null);
  const [paraExcluir, setParaExcluir] = useState(null);

  function acessarAmbiente(clinicId) {
    entrar.mutate(clinicId, {
      onSuccess: async () => {
        await refresh();
        navigate("/dashboard");
      },
    });
  }

  return (
    <MasterShell
      title="Clientes"
      subtitle={`${clientes.length} ${clientes.length === 1 ? "cliente" : "clientes"}`}
      actions={
        podeEscrever && (
          <Button size="sm" className="gap-1.5" onClick={() => setNovoAberto(true)}>
            <Plus className="h-4 w-4" />
            Novo cliente
          </Button>
        )
      }
    >
      <div className="space-y-4 p-6">
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-60 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por razão social, nome fantasia ou CNPJ..."
                className="pl-9"
                aria-label="Buscar clientes"
              />
            </div>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Situação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as situações</SelectItem>
                {CLIENT_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Cliente</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Mensalidade</TableHead>
                <TableHead>Uso</TableHead>
                <TableHead>Renovação</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center">
                    <Spinner className="mx-auto h-6 w-6 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : clientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState
                      icon={Building2}
                      title="Nenhum cliente encontrado"
                      description="Ajuste os filtros ou cadastre um novo cliente."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                clientes.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell>
                      <p className="font-medium leading-tight">{cliente.tradeName}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {cliente.id} · {cliente.company}
                      </p>
                    </TableCell>

                    <TableCell className="text-sm">{cliente.planName}</TableCell>

                    <TableCell>
                      <ClientStatusBadge status={cliente.status} />
                    </TableCell>

                    <TableCell className="text-sm">{formatCurrency(cliente.monthlyValue)}</TableCell>

                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {cliente.metrics.users} usuários · {cliente.metrics.leads} leads
                      <br />
                      {cliente.metrics.automations} jornadas ·{" "}
                      {cliente.whatsappConfigured ? "WhatsApp ativo" : "sem WhatsApp"}
                    </TableCell>

                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(cliente.renewalDate)}
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          onClick={() => acessarAmbiente(cliente.id)}
                          disabled={entrar.isPending}
                        >
                          <LogIn className="h-3.5 w-3.5" />
                          Acessar
                        </Button>

                        {podeEscrever && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setEmEdicao(cliente)}>
                                Editar cadastro
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => setParaExcluir(cliente)}
                              >
                                <Trash2 className="h-4 w-4" />
                                Excluir cliente
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <ClientFormDialog aberto={novoAberto} onClose={() => setNovoAberto(false)} />
      <ClientFormDialog
        aberto={Boolean(emEdicao)}
        cliente={emEdicao}
        onClose={() => setEmEdicao(null)}
      />

      <AlertDialog open={Boolean(paraExcluir)} onOpenChange={(e) => !e && setParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os dados de <strong>{paraExcluir?.tradeName}</strong> serão apagados:
              usuários, leads, conversas, anotações e jornadas. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setParaExcluir(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={excluir.isPending}
              onClick={(evento) => {
                evento.preventDefault();
                excluir.mutate(paraExcluir.id, { onSuccess: () => setParaExcluir(null) });
              }}
            >
              {excluir.isPending ? <Spinner /> : null}
              Excluir tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MasterShell>
  );
}

function ClientFormDialog({ aberto, cliente, onClose }) {
  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onClose()}>
      <DialogContent className="max-w-2xl">
        {aberto && (
          <FormularioCliente key={cliente?.id ?? "novo"} cliente={cliente} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormularioCliente({ cliente, onClose }) {
  const editando = Boolean(cliente);
  const criar = useCreateClient();
  const atualizar = useUpdateClient();
  const { data: planos = [] } = useMasterPlans();

  const mutation = editando ? atualizar : criar;

  const [form, setForm] = useState(() => ({
    company: cliente?.company ?? "",
    tradeName: cliente?.tradeName ?? "",
    cnpj: cliente?.cnpj ?? "",
    ownerName: cliente?.ownerName ?? "",
    email: cliente?.email ?? "",
    phone: cliente?.phone ?? "",
    planId: cliente?.planId ?? "",
    status: cliente?.status ?? "demonstracao",
    monthlyValue: String(cliente?.monthlyValue ?? 0),
    contractDate: cliente?.contractDate ?? "",
    renewalDate: cliente?.renewalDate ?? "",
  }));

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));
  const setInput = (campo) => (e) => set(campo)(e.target.value);

  /** Escolher um plano sugere a mensalidade correspondente. */
  function escolherPlano(planId) {
    const plano = planos.find((p) => p.id === planId);
    setForm((atual) => ({
      ...atual,
      planId,
      monthlyValue: plano ? String(plano.price) : atual.monthlyValue,
    }));
  }

  function submeter(evento) {
    evento.preventDefault();
    const dados = { ...form, monthlyValue: Number(form.monthlyValue) || 0 };
    mutation.mutate(editando ? { id: cliente.id, ...dados } : dados, { onSuccess: onClose });
  }

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>{editando ? "Editar cliente" : "Novo cliente"}</DialogTitle>
        <DialogDescription>Dados cadastrais e comerciais do contrato.</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-4 sm:grid-cols-2">
        <Campo label="Razão social" id="company" className="sm:col-span-2">
          <Input id="company" required value={form.company} onChange={setInput("company")} />
        </Campo>

        <Campo label="Nome fantasia" id="tradeName">
          <Input id="tradeName" required value={form.tradeName} onChange={setInput("tradeName")} />
        </Campo>

        <Campo label="CNPJ" id="cnpj">
          <Input id="cnpj" value={form.cnpj} onChange={setInput("cnpj")} placeholder="00.000.000/0001-00" />
        </Campo>

        <Campo label="Responsável" id="ownerName">
          <Input id="ownerName" value={form.ownerName} onChange={setInput("ownerName")} />
        </Campo>

        <Campo label="E-mail" id="email">
          <Input id="email" type="email" value={form.email} onChange={setInput("email")} />
        </Campo>

        <Campo label="Telefone" id="phone">
          <Input id="phone" value={form.phone} onChange={setInput("phone")} />
        </Campo>

        <Campo label="Plano">
          <Select value={form.planId} onValueChange={escolherPlano}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {planos.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} — {formatCurrency(p.price)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo label="Situação">
          <Select value={form.status} onValueChange={set("status")}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CLIENT_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo label="Mensalidade (R$)" id="monthlyValue">
          <Input
            id="monthlyValue"
            type="number"
            min="0"
            step="0.01"
            value={form.monthlyValue}
            onChange={setInput("monthlyValue")}
          />
        </Campo>

        <Campo label="Início do contrato" id="contractDate">
          <Input id="contractDate" type="date" value={form.contractDate ?? ""} onChange={setInput("contractDate")} />
        </Campo>

        <Campo label="Renovação" id="renewalDate">
          <Input id="renewalDate" type="date" value={form.renewalDate ?? ""} onChange={setInput("renewalDate")} />
        </Campo>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner /> : null}
          {editando ? "Salvar" : "Cadastrar"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function Campo({ label, id, className, children }) {
  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
